import React, { useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { QRCodeCanvas } from "qrcode.react";
import bdsleepLogo from "../../../images/logo/logo_sq.png";
import "./PatientPortalShareModal.css";

const PatientPortalShareModal = ({ onHide }) => {
  const qrRef = useRef(null);
  const linkRef = useRef(null);
  const [message, setMessage] = useState("");
  const configuredPortalUrl = process.env.REACT_APP_PATIENT_PORTAL_URL;
  const portalOrigin = !configuredPortalUrl || [
    "https://sleepy-patient-demo-9b23f.web.app",
    "https://sleepy-patient-demo-9b23f.web.app/",
    "https://sleepy-patient-portal-9b23f.web.app",
    "https://sleepy-patient-portal-9b23f.web.app/",
  ].includes(configuredPortalUrl)
    ? "https://sleepcare.bdsleep.tech/"
    : configuredPortalUrl;
  let url = "";
  try {
    const portalUrl = new URL(portalOrigin);
    if (["https:", "http:"].includes(portalUrl.protocol) && !portalUrl.username && !portalUrl.password) {
      url = `${portalUrl.origin}/`;
    }
  } catch { /* Display the unavailable-link message below. */ }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setMessage("Link copied. You can paste it into a message to the patient.");
    } catch {
      if (linkRef.current) {
        linkRef.current.focus();
        linkRef.current.select();
      }
      setMessage("Please copy the selected link manually.");
    }
  };

  const downloadQr = () => {
    const canvas = qrRef.current && qrRef.current.querySelector("canvas");
    if (!canvas) return;
    const logo = qrRef.current.querySelector("img");
    if (logo && (!logo.complete || !logo.naturalWidth)) {
      setMessage("The QR code is loading. Please try downloading again shortly.");
      return;
    }
    try {
      const anchor = document.createElement("a");
      anchor.href = canvas.toDataURL("image/png");
      anchor.download = "bdsleep-patient-portal-qr.png";
      anchor.click();
    } catch {
      setMessage("Unable to download the QR code. Please try again.");
    }
  };

  return (
    <Modal
      show
      onHide={onHide}
      centered
      id="patient-portal-share-dialog"
      dialogClassName="patient-portal-share-dialog"
      aria-labelledby="patient-portal-share-title"
    >
      <Modal.Header closeButton>
        <div className="patient-portal-share-heading">
          <span className="patient-portal-share-heading__icon" aria-hidden="true">
            <i className="fa fa-share-alt" />
          </span>
          <div>
            <Modal.Title id="patient-portal-share-title">Share Patient Portal</Modal.Title>
            <p>Send a link or QR code to your patient.</p>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {url ? <div className="patient-portal-share-content">
          <div className="patient-portal-share-qr">
            <div ref={qrRef} className="patient-portal-share-qr__image">
              <QRCodeCanvas
                value={url}
                size={224}
                marginSize={4}
                level="H"
                title="BDSleep Patient Portal"
                imageSettings={{ src: bdsleepLogo, width: 44, height: 44, excavate: true }}
                style={{ maxWidth: "100%", height: "auto" }}
              />
            </div>
            <p>Scan to open the portal</p>
            <Button variant="outline-primary" className="patient-portal-share-action patient-portal-share-download" onClick={downloadQr}>
              <i className="fa fa-download" aria-hidden="true" />
              Download QR
            </Button>
          </div>
          <div className="patient-portal-share-link">
            <label className="patient-portal-share-link__label">
              Patient Portal link
              <input
                ref={linkRef}
                className="form-control"
                aria-label="Patient Portal link"
                readOnly
                value={url}
                onFocus={(event) => event.target.select()}
              />
            </label>
            <p className="patient-portal-share-link__hint">Copy the link to send through LINE or a message.</p>
            <Button variant="primary" className="patient-portal-share-action patient-portal-share-copy" onClick={copyLink}>
              <i className="fa fa-copy" aria-hidden="true" />
              Copy link
            </Button>
            <a className="patient-portal-share-open" href={url} target="_blank" rel="noopener noreferrer">
              Open portal
              <i className="fa fa-external-link" aria-hidden="true" />
            </a>
            {message && <p role="status" className="patient-portal-share-feedback">{message}</p>}
          </div>
        </div> : <div role="alert" className="alert alert-warning mb-0">
          The Patient Portal link is unavailable. Please contact the system administrator.
        </div>}
        <div className="patient-portal-share-guides" aria-label="Patient Portal admin guide">
          <a className="btn btn-outline-primary patient-portal-share-guide" href="/guides/patient-portal-admin/Patient_Portal_Admin_Guide_Source/Patient_Portal_Admin_Guide_TH.html" target="_blank" rel="noopener noreferrer">
            <i className="fa fa-book" aria-hidden="true" />
            คู่มือการใช้งาน Patient Portal สำหรับเจ้าหน้าที่
          </a>
          <a className="patient-portal-share-guide-pdf" href="/guides/patient-portal-admin/Patient_Portal_Admin_Guide_TH.pdf" download>
            <i className="fa fa-file-pdf-o" aria-hidden="true" /> ดาวน์โหลดคู่มือ PDF
          </a>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" className="patient-portal-share-close" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PatientPortalShareModal;
